import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Clock, Settings, RefreshCw, Bell, AlertTriangle, Users, CalendarDays, Edit, Mail, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getErrorMessage = (error, fallback = 'Operation failed') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return fallback;
};

export default function DeadlineMonitor() {
  const [deadlineStatus, setDeadlineStatus] = useState(null);
  const [allAppraisalForms, setAllAppraisalForms] = useState([]);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [newDeadlineDate, setNewDeadlineDate] = useState('');
  const [employeeDeadlineDays, setEmployeeDeadlineDays] = useState(15);
  const [managerDeadlineDays, setManagerDeadlineDays] = useState(15);
  const [updateExistingForms, setUpdateExistingForms] = useState(false);
  const [notifyEmployees, setNotifyEmployees] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [deadlineLoading, setDeadlineLoading] = useState(true);
  const [deadlineError, setDeadlineError] = useState('');

  useEffect(() => {
    fetchDeadlineStatus();
    fetchAllAppraisalForms();
  }, []);

  const fetchDeadlineStatus = async () => {
    setDeadlineLoading(true);
    setDeadlineError('');
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/deadline-status`, config);
      setDeadlineStatus(response.data);
    } catch (error) {
      console.error('Error fetching deadline status:', error);
      setDeadlineStatus(null);
      setDeadlineError(getErrorMessage(error, 'Unable to load deadline status'));
    } finally {
      setDeadlineLoading(false);
    }
  };

  const fetchAllAppraisalForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/all-forms`, config);
      setAllAppraisalForms(response.data);
    } catch (error) {
      console.error('Error fetching appraisal forms:', error);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/appraisal/trigger-reminders`, {}, config);
      toast.success('Deadline reminders triggered successfully');
      fetchDeadlineStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to trigger reminders'));
    } finally {
      setTriggeringReminders(false);
    }
  };

  const openDeadlineEditor = (form, deadlineType) => {
    setEditingDeadline({ form, deadlineType });
    const currentDeadline = deadlineType === 'employee' ? form.employee_deadline : form.manager_deadline;
    setNewDeadlineDate(currentDeadline ? currentDeadline.slice(0, 10) : '');
    setShowDeadlineDialog(true);
  };

  const handleUpdateDeadline = async () => {
    if (!editingDeadline || !newDeadlineDate) {
      toast.error('Please select a new deadline date');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/appraisal/forms/${editingDeadline.form.id}/update-deadline`, {
        deadline_type: editingDeadline.deadlineType,
        new_deadline: newDeadlineDate
      }, config);
      toast.success('Deadline updated successfully');
      setShowDeadlineDialog(false);
      setEditingDeadline(null);
      setNewDeadlineDate('');
      fetchDeadlineStatus();
      fetchAllAppraisalForms();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update deadline'));
    }
  };

  const handleUpdateSettings = async () => {
    setUpdatingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/appraisal/settings/update-deadline-days`, {
        employee_deadline_days: employeeDeadlineDays,
        manager_deadline_days: managerDeadlineDays,
        update_existing_forms: updateExistingForms,
        notify_employees: notifyEmployees
      }, config);
      toast.success(`Settings updated! ${response.data.updated_forms} forms updated, ${response.data.notifications_queued} notifications queued`);
      setShowSettingsDialog(false);
      fetchDeadlineStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update deadline settings'));
    } finally {
      setUpdatingSettings(false);
    }
  };

  return (
    <>
      <Card data-testid="deadline-monitor-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Appraisal Deadline Monitor
            </CardTitle>
            <CardDescription>Track and manage appraisal deadlines. Reminders run automatically at 9:00 AM daily.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSettingsDialog(true)} data-testid="configure-deadlines-btn">
              <Settings className="w-4 h-4 mr-2" /> Configure
            </Button>
            <Button variant="outline" onClick={fetchDeadlineStatus} data-testid="refresh-deadlines-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button onClick={handleTriggerReminders} disabled={triggeringReminders} className="bg-orange-600 hover:bg-orange-700" data-testid="trigger-reminders-btn">
              <Bell className="w-4 h-4 mr-2" /> {triggeringReminders ? 'Sending...' : 'Send Reminders Now'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {deadlineLoading ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 mx-auto text-gray-300 mb-4 animate-pulse" />
              <p className="text-gray-500" data-testid="deadline-status-loading">Loading deadline status...</p>
            </div>
          ) : deadlineError ? (
            <div className="border border-red-200 bg-red-50 p-5 text-center" data-testid="deadline-status-error">
              <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
              <p className="font-medium text-red-800">{deadlineError}</p>
              <Button variant="outline" className="mt-3" onClick={fetchDeadlineStatus} data-testid="retry-deadline-status-button">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /><span className="font-medium">Employee Overdue</span></div>
                  <p className="text-3xl font-bold text-red-600 mt-2">{deadlineStatus.summary.employee_overdue}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 text-orange-700"><Clock className="w-5 h-5" /><span className="font-medium">Employee Urgent</span></div>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{deadlineStatus.summary.employee_urgent}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /><span className="font-medium">Manager Overdue</span></div>
                  <p className="text-3xl font-bold text-red-600 mt-2">{deadlineStatus.summary.manager_overdue}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 text-orange-700"><Clock className="w-5 h-5" /><span className="font-medium">Manager Urgent</span></div>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{deadlineStatus.summary.manager_urgent}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Employee Self-Assessment Deadlines</h3>
                {deadlineStatus.employee_deadlines.length === 0 ? (
                  <div className="bg-green-50 p-4 rounded-lg text-green-700 text-center">No pending employee deadlines</div>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Fiscal Year</TableHead><TableHead>Deadline</TableHead><TableHead className="text-center">Days Remaining</TableHead><TableHead className="text-center">Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {deadlineStatus.employee_deadlines.map((d, idx) => (
                        <TableRow key={idx} className={d.status === 'overdue' ? 'bg-red-50' : d.status === 'urgent' ? 'bg-orange-50' : ''}>
                          <TableCell className="font-medium">{d.employee_name}</TableCell>
                          <TableCell>{d.fiscal_year}</TableCell>
                          <TableCell>{d.deadline}</TableCell>
                          <TableCell className="text-center"><span className={`font-bold ${d.days_remaining < 0 ? 'text-red-600' : d.days_remaining <= 1 ? 'text-orange-600' : ''}`}>{d.days_remaining < 0 ? `${Math.abs(d.days_remaining)} overdue` : d.days_remaining}</span></TableCell>
                          <TableCell className="text-center"><Badge className={d.status === 'overdue' ? 'bg-red-600' : d.status === 'urgent' ? 'bg-orange-500' : d.status === 'approaching' ? 'bg-yellow-500' : 'bg-green-500'}>{d.status.replace('_', ' ')}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Manager Review Deadlines</h3>
                {deadlineStatus.manager_deadlines.length === 0 ? (
                  <div className="bg-green-50 p-4 rounded-lg text-green-700 text-center">No pending manager review deadlines</div>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Manager</TableHead><TableHead>Fiscal Year</TableHead><TableHead>Deadline</TableHead><TableHead className="text-center">Days Remaining</TableHead><TableHead className="text-center">Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {deadlineStatus.manager_deadlines.map((d, idx) => (
                        <TableRow key={idx} className={d.status === 'overdue' ? 'bg-red-50' : d.status === 'urgent' ? 'bg-orange-50' : ''}>
                          <TableCell className="font-medium">{d.employee_name}</TableCell>
                          <TableCell>{d.manager_name}</TableCell>
                          <TableCell>{d.fiscal_year}</TableCell>
                          <TableCell>{d.deadline}</TableCell>
                          <TableCell className="text-center"><span className={`font-bold ${d.days_remaining < 0 ? 'text-red-600' : d.days_remaining <= 1 ? 'text-orange-600' : ''}`}>{d.days_remaining < 0 ? `${Math.abs(d.days_remaining)} overdue` : d.days_remaining}</span></TableCell>
                          <TableCell className="text-center"><Badge className={d.status === 'overdue' ? 'bg-red-600' : d.status === 'urgent' ? 'bg-orange-500' : d.status === 'approaching' ? 'bg-yellow-500' : 'bg-green-500'}>{d.status.replace('_', ' ')}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2"><Bell className="w-4 h-4" /> Automatic Reminders</h4>
                <p className="text-sm text-blue-700">Email reminders are automatically sent daily at 9:00 AM for:</p>
                <ul className="text-sm text-blue-600 mt-2 ml-4 list-disc">
                  <li>3 days before deadline</li>
                  <li>1 day before deadline</li>
                  <li>On the deadline day</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> All Appraisal Forms - Edit Deadlines</h3>
                {allAppraisalForms.length === 0 ? (
                  <div className="bg-gray-50 p-4 rounded-lg text-gray-500 text-center">No appraisal forms found</div>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Manager</TableHead><TableHead>FY</TableHead><TableHead className="text-center">Status</TableHead><TableHead>Employee Deadline</TableHead><TableHead>Manager Deadline</TableHead><TableHead className="text-center">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {allAppraisalForms.map(form => (
                        <TableRow key={form.id}>
                          <TableCell className="font-medium">{form.employee_name}</TableCell>
                          <TableCell>{form.manager_name}</TableCell>
                          <TableCell>{form.fiscal_year}</TableCell>
                          <TableCell className="text-center"><Badge className={form.status === 'completed' ? 'bg-green-500' : form.status === 'pending_hr' ? 'bg-purple-500' : form.status === 'pending_appraiser' ? 'bg-yellow-500' : form.status === 'pending_appraisee_review' ? 'bg-blue-500' : 'bg-gray-400'}>{form.status.replace(/_/g, ' ')}</Badge></TableCell>
                          <TableCell>{form.employee_deadline ? form.employee_deadline.slice(0, 10) : '-'}</TableCell>
                          <TableCell>{form.manager_deadline ? form.manager_deadline.slice(0, 10) : '-'}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex gap-1 justify-center">
                              {form.status === 'draft' && form.employee_deadline && (<Button variant="outline" size="sm" onClick={() => openDeadlineEditor(form, 'employee')} className="text-blue-600"><Edit className="w-3 h-3 mr-1" /> Emp</Button>)}
                              {form.status === 'pending_appraiser' && form.manager_deadline && (<Button variant="outline" size="sm" onClick={() => openDeadlineEditor(form, 'manager')} className="text-orange-600"><Edit className="w-3 h-3 mr-1" /> Mgr</Button>)}
                              {form.status !== 'draft' && form.status !== 'pending_appraiser' && <span className="text-xs text-gray-400">-</span>}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Deadline Dialog */}
      <Dialog open={showDeadlineDialog} onOpenChange={(open) => { setShowDeadlineDialog(open); if (!open) { setEditingDeadline(null); setNewDeadlineDate(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Deadline</DialogTitle></DialogHeader>
          {editingDeadline && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm"><strong>Employee:</strong> {editingDeadline.form.employee_name}</p>
                <p className="text-sm"><strong>Manager:</strong> {editingDeadline.form.manager_name}</p>
                <p className="text-sm"><strong>Fiscal Year:</strong> {editingDeadline.form.fiscal_year}</p>
                <p className="text-sm"><strong>Deadline Type:</strong> {editingDeadline.deadlineType === 'employee' ? 'Employee Self-Assessment' : 'Manager Review'}</p>
              </div>
              <div><Label>New Deadline Date</Label><Input type="date" value={newDeadlineDate} onChange={(e) => setNewDeadlineDate(e.target.value)} className="mt-1" /></div>
              <Button onClick={handleUpdateDeadline} className="w-full">Update Deadline</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deadline Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Configure Deadline Settings</DialogTitle>
            <DialogDescription>Set the number of days for appraisal deadlines and optionally update existing forms.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> Employee Self-Assessment Days</Label>
                <Input type="number" value={employeeDeadlineDays} onChange={(e) => setEmployeeDeadlineDays(parseInt(e.target.value) || 15)} min={1} max={90} className="mt-1" />
                <p className="text-xs text-gray-500 mt-1">Days given to employees to complete self-assessment after publishing</p>
              </div>
              <div>
                <Label className="flex items-center gap-2"><Users className="w-4 h-4 text-green-500" /> Manager Review Days</Label>
                <Input type="number" value={managerDeadlineDays} onChange={(e) => setManagerDeadlineDays(parseInt(e.target.value) || 15)} min={1} max={90} className="mt-1" />
                <p className="text-xs text-gray-500 mt-1">Days given to managers to complete review after end of employee self assessment deadline</p>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
              <p className="font-medium text-gray-700">Apply to Existing Forms</p>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="updateExisting" checked={updateExistingForms} onChange={(e) => setUpdateExistingForms(e.target.checked)} className="w-4 h-4 rounded border-gray-300" />
                <Label htmlFor="updateExisting" className="cursor-pointer text-sm">Update deadlines for existing published forms</Label>
              </div>
              {updateExistingForms && (
                <div className="flex items-center space-x-2 ml-6">
                  <input type="checkbox" id="notifyEmployees" checked={notifyEmployees} onChange={(e) => setNotifyEmployees(e.target.checked)} className="w-4 h-4 rounded border-gray-300" />
                  <Label htmlFor="notifyEmployees" className="cursor-pointer text-sm flex items-center gap-1"><Mail className="w-3 h-3" /> Send email notifications about updated deadlines</Label>
                </div>
              )}
              {updateExistingForms && (
                <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded text-sm text-yellow-800"><AlertCircle className="w-4 h-4" /> This will recalculate deadlines for all active appraisal forms</div>
              )}
            </div>
            <Button onClick={handleUpdateSettings} className="w-full" disabled={updatingSettings}>{updatingSettings ? 'Updating...' : 'Save Settings'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
